/**
 * Modal to edit an existing user.
 *
 * Extracted from UserManagementPage.tsx (Sub-PR 6.3).
 */

import { X, Loader2, Check } from 'lucide-react'
import type { CreateUserForm, UserManagementOrganisation } from './types'
import { UserFormFields } from './UserFormFields'

interface EditUserModalProps {
  formData: CreateUserForm
  setFormData: (data: CreateUserForm) => void
  formError: string | null
  formLoading: boolean
  isPlatformAdmin: boolean
  organisations: UserManagementOrganisation[]
  onClose: () => void
  onSubmit: (e: React.FormEvent) => void
}

export function EditUserModal({
  formData,
  setFormData,
  formError,
  formLoading,
  isPlatformAdmin,
  organisations,
  onClose,
  onSubmit,
}: EditUserModalProps): React.ReactElement {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl dark:bg-stone-800">
        <div className="flex items-center justify-between border-b border-stone-200 p-4 dark:border-stone-700">
          <h2 className="text-lg font-semibold text-stone-900 dark:text-stone-100">Edit User</h2>
          <button
            onClick={onClose}
            className="rounded p-1 hover:bg-stone-100 dark:hover:bg-stone-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={onSubmit} className="space-y-4 p-4">
          {formError && (
            <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400">
              {formError}
            </div>
          )}
          <UserFormFields
            formData={formData}
            setFormData={setFormData}
            isPlatformAdmin={isPlatformAdmin}
            organisations={organisations}
            showCredentials={false}
          />
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg px-4 py-2 text-stone-700 hover:bg-stone-100 dark:text-stone-300 dark:hover:bg-stone-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={formLoading}
              className="flex items-center gap-2 rounded-lg bg-[#4e5e82] px-4 py-2 text-white hover:bg-[#5c6e94] disabled:opacity-50"
            >
              {formLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )}
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
